import React from 'react';
import cx from 'clsx';
import { flow, groupBy, sortBy, entries, map, filter } from 'lodash/fp';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableContainer from '@material-ui/core/TableContainer';
import TableHead from '@material-ui/core/TableHead';
import TableRow from '@material-ui/core/TableRow';
import Popover from 'components/@common/Popover';
import { makeStyles } from '@material-ui/core/styles';
import useProductCache from 'store/cache/product-cache';
import { getId, sortProductByGrades, sortProductCurriculumByGrades } from 'utilities/product-utils';
import { OrganizationLicenseUsage, Product, ProductCurriculum } from 'types/schema-generated-types';
import { useTranslation } from 'react-i18next';

const useStyles = makeStyles(theme => ({
  container: {
    padding: 1
  },
  tableBody: {
    borderRadius: 4,
    boxShadow: '0 0 0 1px #e0e0e0',
    '& > tr:last-child > td': {
      borderBottom: 'none'
    }
  },
  noBorder: {
    borderBottom: 'none'
  },
  expiringSoonText: {
    color: theme.palette.error.main
  },
  licenseCell: {
    paddingLeft: theme.spacing(1),
    paddingRight: theme.spacing(1)
  }
}));

interface ProductsTableProps {
  licenseUsage: OrganizationLicenseUsage[];
}

const ProductsTable: React.FC<ProductsTableProps> = ({ licenseUsage }) => {
  const classes = useStyles();
  const [{ findProductByProductCode }] = useProductCache();
  const { t } = useTranslation(['ProductAndLicenses']);

  const licenseUsageByProduct = flow(
    sortBy<OrganizationLicenseUsage>('product.code'),
    groupBy('product.code'),
    entries,
    map(([productCode, usage]: [string, OrganizationLicenseUsage[]]) => {
      const product = findProductByProductCode(productCode);
      return [product, usage];
    }),
    filter(([product]) => !!product)
  )(licenseUsage) as [Product, OrganizationLicenseUsage[]][];

  const rows = licenseUsageByProduct
    .sort(([a], [b]) => sortProductByGrades(a, b))
    .flatMap(([product, usages]: [Product, OrganizationLicenseUsage[]]) => {
      if (!product || !product.curriculums) return [];

      const items: {
        product: Product;
        productCurriculum: ProductCurriculum;
        usage: OrganizationLicenseUsage;
      }[] = [];

      product.curriculums.forEach(productCurriculum => {
        const usage = usages.find(lu => lu.productCurriculum?.id === productCurriculum.id);
        if (usage) items.push({ product, productCurriculum, usage });
      });

      return items.sort((a, b) =>
        sortProductCurriculumByGrades(a.productCurriculum, b.productCurriculum)
      );
    });

  return (
    <TableContainer className={classes.container}>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell
              style={{ minWidth: '180px' }}
              className={cx(classes.noBorder, classes.licenseCell)}
            >
              License
            </TableCell>
            <TableCell
              align="left"
              className={cx(classes.noBorder, classes.licenseCell)}
              style={{ whiteSpace: 'nowrap' }}
            >
              <Popover description={t('assignedInfo')} centerIcon={false}>
                Assigned
              </Popover>
            </TableCell>
            <TableCell
              align="left"
              className={cx(classes.noBorder, classes.licenseCell)}
              style={{ whiteSpace: 'nowrap' }}
            >
              <Popover description={t('expSoonInfo')} centerIcon={false}>
                Exp. Soon
              </Popover>
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody className={classes.tableBody}>
          {rows.map(({ product, productCurriculum, usage: { assignedCount, expiringCount } }) => (
            <TableRow key={getId(product, productCurriculum)}>
              <TableCell className={cx(classes.licenseCell)}>
                {productCurriculum.code}: {productCurriculum.name}
              </TableCell>
              <TableCell align="left" className={cx(classes.licenseCell)}>
                {assignedCount}
              </TableCell>
              <TableCell align="left" className={cx(classes.expiringSoonText, classes.licenseCell)}>
                {expiringCount}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default ProductsTable;
